import { NotionBlock } from "@/hooks/useNotionInsights";

interface NotionBlockRendererProps {
  blocks: NotionBlock[];
}

function extractRichText(richTextArray: Array<{ plain_text: string; annotations?: any; href?: string }> | undefined): JSX.Element[] {
  if (!richTextArray || richTextArray.length === 0) {
    return [];
  }

  return richTextArray.map((text, index) => {
    let content: JSX.Element = <span key={index}>{text.plain_text}</span>;
    
    if (text.annotations) {
      if (text.annotations.bold) {
        content = <strong key={index}>{text.plain_text}</strong>;
      }
      if (text.annotations.italic) {
        content = <em key={index}>{text.plain_text}</em>;
      }
      if (text.annotations.code) {
        content = <code key={index} className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">{text.plain_text}</code>;
      }
      if (text.annotations.strikethrough) {
        content = <s key={index}>{text.plain_text}</s>;
      }
    }
    
    if (text.href) {
      content = <a key={index} href={text.href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">{text.plain_text}</a>;
    }
    
    return content;
  });
}

function RenderBlock({ block }: { block: NotionBlock }) {
  switch (block.type) {
    case 'paragraph':
      const paragraphText = extractRichText(block.paragraph?.rich_text);
      if (paragraphText.length === 0) return <div className="h-4" />;
      return <p className="text-muted-foreground leading-relaxed mb-4">{paragraphText}</p>;

    case 'heading_1':
      return <h1 className="font-display text-3xl font-bold text-foreground mt-8 mb-4">{extractRichText(block.heading_1?.rich_text)}</h1>;

    case 'heading_2':
      return <h2 className="font-display text-2xl font-bold text-foreground mt-6 mb-3">{extractRichText(block.heading_2?.rich_text)}</h2>;

    case 'heading_3':
      return <h3 className="font-display text-xl font-semibold text-foreground mt-4 mb-2">{extractRichText(block.heading_3?.rich_text)}</h3>;

    case 'bulleted_list_item':
      return <li className="text-muted-foreground ml-4 mb-1">{extractRichText(block.bulleted_list_item?.rich_text)}</li>;

    case 'numbered_list_item':
      return <li className="text-muted-foreground ml-4 mb-1">{extractRichText(block.numbered_list_item?.rich_text)}</li>;

    case 'quote':
      return (
        <blockquote className="border-l-4 border-primary pl-4 my-4 italic text-muted-foreground">
          {extractRichText(block.quote?.rich_text)}
        </blockquote>
      );

    case 'code':
      return (
        <pre className="bg-muted p-4 rounded-lg overflow-x-auto my-4">
          <code className="text-sm font-mono text-foreground">
            {block.code?.rich_text?.map((t: any) => t.plain_text).join('')}
          </code>
        </pre>
      );

    case 'divider':
      return <hr className="border-border my-8" />;

    case 'image':
      const imageUrl = block.image?.file?.url || block.image?.external?.url;
      const caption = block.image?.caption?.[0]?.plain_text;
      return (
        <figure className="my-6">
          <img 
            src={imageUrl} 
            alt={caption || ''} 
            className="rounded-lg w-full"
          />
          {caption && (
            <figcaption className="text-sm text-muted-foreground text-center mt-2">
              {caption}
            </figcaption>
          )}
        </figure>
      );

    case 'callout':
      return (
        <div className="bg-card border border-border rounded-lg p-4 my-4 flex gap-3">
          <span>{block.callout?.icon?.emoji || '💡'}</span>
          <div className="text-muted-foreground">{extractRichText(block.callout?.rich_text)}</div>
        </div>
      );

    case 'toggle':
      return (
        <details className="my-2">
          <summary className="cursor-pointer text-foreground font-medium">
            {extractRichText(block.toggle?.rich_text)}
          </summary>
        </details>
      );

    default:
      console.log('Unhandled block type:', block.type);
      return null;
  }
}

export function NotionBlockRenderer({ blocks }: NotionBlockRendererProps) {
  // Group list items together
  const groupedBlocks: (NotionBlock | NotionBlock[])[] = [];
  let currentList: NotionBlock[] = [];
  let currentListType: string | null = null;

  blocks.forEach((block, index) => {
    if (block.type === 'bulleted_list_item' || block.type === 'numbered_list_item') {
      if (currentListType === block.type) {
        currentList.push(block);
      } else {
        if (currentList.length > 0) {
          groupedBlocks.push([...currentList]);
        }
        currentList = [block];
        currentListType = block.type;
      }
    } else {
      if (currentList.length > 0) {
        groupedBlocks.push([...currentList]);
        currentList = [];
        currentListType = null;
      }
      groupedBlocks.push(block);
    }
  });

  // Don't forget the last list
  if (currentList.length > 0) {
    groupedBlocks.push(currentList);
  }

  return (
    <div className="prose prose-lg max-w-none">
      {groupedBlocks.map((item, index) => {
        if (Array.isArray(item)) {
          const isNumbered = item[0]?.type === 'numbered_list_item';
          const ListTag = isNumbered ? 'ol' : 'ul';
          return (
            <ListTag key={index} className={`my-4 ${isNumbered ? 'list-decimal' : 'list-disc'} pl-4`}>
              {item.map((block) => (
                <RenderBlock key={block.id} block={block} />
              ))}
            </ListTag>
          );
        }
        return <RenderBlock key={item.id} block={item} />;
      })}
    </div>
  );
}
